import type { Account, RollupSnapshot } from '@taler/api-client'
import { type Locale, type Money, resolveLocalized } from '@taler/domain'

export interface AccountRollupRow {
  readonly kind: 'account'
  /** The real account id: safe to look up in the accounts list, unlike an 'other' row's id. */
  readonly id: string
  readonly depth: number
  readonly label: string
  readonly hasChildren: boolean
  readonly ownBalance: Money
  readonly rolledUpBalance: Money
}

/**
 * NOT an account: no real account id, no chevron, no context menu. Purely derived from
 * `otherByParent` - postings that landed directly on a parent before it grew children.
 * See AGENTS.md's "The parent-rollup 'Egyéb' row".
 */
export interface OtherRollupRow {
  readonly kind: 'other'
  readonly id: `other:${string}`
  readonly parentId: string
  readonly depth: number
  readonly amount: Money
}

export type RollupTreeRow = AccountRollupRow | OtherRollupRow

/** Pre-order walk: real account rows first, each parent's "Egyéb" row right after it. */
export function buildRollupTree(
  rollup: RollupSnapshot,
  accounts: readonly Account[],
  locale: Locale,
): RollupTreeRow[] {
  const accountById = new Map(accounts.map((account) => [account.id, account]))
  const rowById = new Map(rollup.rows.map((row) => [row.accountId, row]))
  const childrenOf = new Map<string, string[]>()
  for (const row of rollup.rows) {
    if (row.parentId === undefined) continue
    childrenOf.set(row.parentId, [...(childrenOf.get(row.parentId) ?? []), row.accountId])
  }

  const labelFor = (id: string): string => {
    const account = accountById.get(id)
    return account === undefined ? id : (resolveLocalized(account.names, locale)?.name ?? account.code)
  }

  const out: RollupTreeRow[] = []
  const walk = (accountId: string, depth: number): void => {
    const row = rowById.get(accountId)
    if (row === undefined) return
    const children = (childrenOf.get(accountId) ?? []).sort((a, b) => labelFor(a).localeCompare(labelFor(b)))
    out.push({
      kind: 'account',
      id: accountId,
      depth,
      label: labelFor(accountId),
      hasChildren: children.length > 0,
      ownBalance: row.ownBalance,
      rolledUpBalance: row.rolledUpBalance,
    })
    const other = rollup.otherByParent.get(accountId)
    if (other !== undefined) {
      out.push({
        kind: 'other',
        id: `other:${accountId}`,
        parentId: accountId,
        depth: depth + 1,
        amount: other,
      })
    }
    for (const child of children) walk(child, depth + 1)
  }

  const roots = rollup.rows.filter((row) => row.parentId === undefined).map((row) => row.accountId)
  for (const root of roots) walk(root, 0)
  return out
}
